/* =========================================================================
   MOTHER MARY MATCH-3 (3 tiers). Reuses the shared pure grid helpers on the
   same 6×7 board; only the rendered glyphs differ. Each tier has its own moves
   + clear-goal; completing a tier advances. Reward scales with total cleared +
   tiers completed. Typed port — identical behavior.
   ========================================================================= */
import { byId, show } from '../dom';
import { ctx } from '../context';
import { fmtDelta, esc, shuffle } from '../engine';
import { sndTick, sndCorrect, sndJackpot, applause } from '../audio';
import { M3_COLS, M3_ROWS, m3at, m3rand, makeBoard, findMatches, swapWouldMatch, hasPossibleMove, type Board } from './match3core';
import { maybeShowTutorial, tutorialInfoButtonHTML } from '../tutorialOverlay';

export const MARY_SYMBOLS = ['🌹', '👑', '⭐', '❤️‍🔥', '💙', '⚜️']; // rose, crown, star, Immaculate Heart (flaming heart), heart, fleur-de-lis

export interface MaryTier {
  name: string;
  icon: string;
  moves: number;
  goal: number;
}

export const MARY_TIERS: MaryTier[] = [
  { name: 'The Annunciation', icon: '🌹', moves: 12, goal: 15 },
  { name: 'Queen of Heaven', icon: '👑', moves: 14, goal: 20 },
  { name: 'Our Lady of Victory', icon: '⭐', moves: 16, goal: 25 },
];

const SKIP_AWARD = 5_000;

export const maryScreenHTML = /* html */ `
<div id="screenMaryMatch3" class="match3 hidden">
  <div class="kicker">Immaculate Garden${tutorialInfoButtonHTML('marymatch3')}</div>
  <h2>Mother Mary Match-3</h2>
  <div class="blurb">Three tiers of grace. Match Marian symbols — <b>🌹 👑 ⭐ ❤️‍🔥 💙 ⚜️</b> — that fall from heaven. Reach each tier's goal to advance.</div>
  <div class="manna-hud">
    <div class="hud-item"><span class="hud-k">Tier</span><span class="hud-v money" id="maryTier">1 / 3</span></div>
    <div class="hud-item"><span class="hud-k">Moves</span><span class="hud-v money" id="maryMoves">12</span></div>
    <div class="hud-item"><span class="hud-k">Goal</span><span class="hud-v money" id="maryGoal">0 / 15</span></div>
  </div>
  <div class="match3-field mary-field" id="maryBoard">
    <div class="mary-banner" id="maryBanner"></div>
  </div>
  <div class="result" id="maryResult"></div>
  <button class="skiplink" id="marySkip">Skip this break →</button>
  <div class="cta"><button class="btn" id="maryBtn">Begin Tier 1</button></div>
</div>`;

let maryAlive = false;
let maryResolving = false;
let maryStarted = false;
let maryDone = false;
let maryTier = 0;
let maryMoves = 0;
let maryTierCleared = 0;
let maryTotalCleared = 0;
let maryGoalsMet = 0;
let marySel = -1;
let maryBoard: Board = [];
let maryEls: (HTMLElement | null)[] = [];

const m3delay = (ms: number): Promise<void> => new Promise((res) => setTimeout(res, ms));

export function maryReward(total: number, goals: number): { cents: number; jackpot: boolean } {
  let cents = 10_000 + total * 500; // $100 + $5 per cleared tile
  cents = Math.max(10_000, Math.min(40_000, cents)); // clamp $100–$400
  cents += goals * 5_000; // +$50 per tier goal met
  const jackpot = goals >= MARY_TIERS.length;
  if (jackpot) cents = Math.max(cents, 50_000); // $500 for completing all three tiers
  return { cents, jackpot };
}

interface Geom {
  size: number;
  ox: number;
  oy: number;
}

function maryGeom(): Geom {
  const b = byId('maryBoard');
  const w = b.clientWidth,
    h = b.clientHeight || (w * M3_ROWS) / M3_COLS;
  const size = Math.floor(Math.min(w / M3_COLS, h / M3_ROWS));
  return { size, ox: Math.round((w - size * M3_COLS) / 2), oy: Math.round((h - size * M3_ROWS) / 2) };
}

function maryClearTiles(): void {
  byId('maryBoard')
    .querySelectorAll('.m3tile')
    .forEach((t) => t.remove());
}

function maryNewTile(v: number): HTMLElement {
  const t = document.createElement('div');
  t.className = 'm3tile';
  t.textContent = MARY_SYMBOLS[v];
  t.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    const idx = maryEls.indexOf(t);
    if (idx >= 0) void maryTap(idx);
  });
  byId('maryBoard').appendChild(t);
  return t;
}

function marySize(t: HTMLElement, g: Geom): void {
  t.style.width = t.style.height = g.size + 'px';
  t.style.fontSize = Math.round(g.size * 0.58) + 'px';
}

function maryRenderAll(): void {
  const g = maryGeom();
  for (let i = 0; i < maryEls.length; i++) {
    const t = maryEls[i];
    if (!t) continue;
    marySize(t, g);
    const r = Math.floor(i / M3_COLS),
      c = i % M3_COLS;
    t.style.setProperty('--pos', `translate(${g.ox + c * g.size}px, ${g.oy + r * g.size}px)`);
    t.classList.toggle('sel', i === marySel);
  }
}

function maryCollapseDOM(): void {
  const g = maryGeom();
  for (let c = 0; c < M3_COLS; c++) {
    let write = M3_ROWS - 1;
    for (let r = M3_ROWS - 1; r >= 0; r--) {
      const i = m3at(r, c);
      if (maryBoard[i] !== null && maryBoard[i] !== undefined) {
        const w = m3at(write, c);
        if (w !== i) {
          maryBoard[w] = maryBoard[i];
          maryEls[w] = maryEls[i];
          maryBoard[i] = null;
          maryEls[i] = null;
        }
        write--;
      }
    }
    for (let r = write; r >= 0; r--) {
      const i = m3at(r, c);
      maryBoard[i] = m3rand();
      const t = maryNewTile(maryBoard[i] as number);
      marySize(t, g);
      t.style.setProperty('--pos', `translate(${g.ox + c * g.size}px, ${g.oy + (r - (write + 1)) * g.size}px)`);
      maryEls[i] = t;
    }
  }
  void byId('maryBoard').offsetWidth;
}

function marySwap(a: number, b: number): void {
  [maryBoard[a], maryBoard[b]] = [maryBoard[b], maryBoard[a]];
  [maryEls[a], maryEls[b]] = [maryEls[b], maryEls[a]];
}

function maryUpdateHud(): void {
  byId('maryTier').textContent = maryTier + 1 + ' / ' + MARY_TIERS.length;
  byId('maryMoves').textContent = String(maryMoves);
  byId('maryGoal').textContent = maryTierCleared + ' / ' + MARY_TIERS[maryTier].goal;
}

export function showMaryMatch3(): void {
  maryAlive = false;
  maryResolving = false;
  maryStarted = false;
  maryDone = false;
  maryTier = 0;
  maryTotalCleared = 0;
  maryGoalsMet = 0;
  marySel = -1;
  maryBoard = [];
  maryClearTiles();
  maryTierCleared = 0;
  maryMoves = MARY_TIERS[0].moves;
  maryUpdateHud();
  byId('maryResult').innerHTML = '';
  const banner = byId('maryBanner');
  banner.classList.remove('show');
  banner.innerHTML = '';
  const btn = byId<HTMLButtonElement>('maryBtn');
  btn.textContent = 'Begin Tier 1';
  btn.disabled = false;
  btn.onclick = startMary;
  show('marymatch3');
  maybeShowTutorial('marymatch3'); // the 3-tier goal structure is unique to this variant
}

export function startMary(): void {
  if (maryStarted) return;
  maryStarted = true;
  maryAlive = true;
  const btn = byId<HTMLButtonElement>('maryBtn');
  btn.disabled = true;
  btn.textContent = 'Praying…';
  byId('maryResult').innerHTML = '';
  byId('marySkip').classList.add('show');
  beginMaryTier();
}

function beginMaryTier(): void {
  const tier = MARY_TIERS[maryTier];
  maryMoves = tier.moves;
  maryTierCleared = 0;
  marySel = -1;
  maryClearTiles();
  do {
    maryBoard = makeBoard();
  } while (findMatches(maryBoard).size > 0 || !hasPossibleMove(maryBoard));
  const g = maryGeom();
  maryEls = new Array(maryBoard.length);
  for (let i = 0; i < maryBoard.length; i++) {
    const r = Math.floor(i / M3_COLS),
      c = i % M3_COLS;
    const t = maryNewTile(maryBoard[i] as number);
    marySize(t, g);
    t.style.setProperty('--pos', `translate(${g.ox + c * g.size}px, ${g.oy - (M3_ROWS - r) * g.size}px)`);
    maryEls[i] = t;
  }
  maryUpdateHud();
  const banner = byId('maryBanner');
  banner.innerHTML = `<div class="mb-icon">${tier.icon}</div><div class="mb-tier">Tier ${maryTier + 1} of ${MARY_TIERS.length}</div><div class="mb-name">${esc(tier.name)}</div><div class="mb-goal">Clear ${tier.goal} to advance</div>`;
  banner.classList.add('show');
  setTimeout(() => {
    if (!maryAlive) return;
    banner.classList.remove('show');
    void byId('maryBoard').offsetWidth;
    requestAnimationFrame(maryRenderAll);
  }, 1500);
}

async function maryTap(i: number): Promise<void> {
  if (!maryAlive || maryResolving || maryMoves <= 0) return;
  if (marySel === -1) {
    marySel = i;
    maryRenderAll();
    return;
  }
  if (marySel === i) {
    marySel = -1;
    maryRenderAll();
    return;
  }
  const r1 = Math.floor(marySel / M3_COLS),
    c1 = marySel % M3_COLS,
    r2 = Math.floor(i / M3_COLS),
    c2 = i % M3_COLS;
  if (Math.abs(r1 - r2) + Math.abs(c1 - c2) !== 1) {
    marySel = i;
    maryRenderAll();
    return;
  }
  const a = marySel,
    b2 = i;
  marySel = -1;
  if (!swapWouldMatch(maryBoard, a, b2)) {
    maryResolving = true;
    marySwap(a, b2);
    maryRenderAll();
    sndTick();
    await m3delay(230);
    if (!maryAlive) return;
    marySwap(a, b2);
    maryRenderAll();
    await m3delay(230);
    if (!maryAlive) return;
    maryResolving = false;
    return;
  }
  maryResolving = true;
  maryMoves--;
  marySwap(a, b2);
  maryRenderAll();
  sndTick();
  maryUpdateHud();
  await m3delay(210);
  if (!maryAlive) return;
  await maryResolve();
  if (!maryAlive) return;

  const goal = MARY_TIERS[maryTier].goal;
  if (maryMoves > 0 && maryTierCleared < goal && !hasPossibleMove(maryBoard)) {
    byId('maryResult').innerHTML = `<div class="r-sub">No moves — Our Lady reshuffles the garden…</div>`;
    maryReshuffle();
    maryRenderAll();
    await m3delay(300);
    if (!maryAlive) return;
    byId('maryResult').innerHTML = '';
  }
  maryResolving = false;
  if (maryTierCleared >= goal || maryMoves <= 0) endMaryTier();
}

function maryReshuffle(): void {
  let order: number[];
  let trial: Board = [];
  do {
    order = shuffle(maryBoard.map((_, i) => i));
    trial = new Array(maryBoard.length);
    for (let k = 0; k < order.length; k++) trial[k] = maryBoard[order[k]];
  } while (findMatches(trial).size > 0 || !hasPossibleMove(trial));
  const nv: Board = new Array(maryBoard.length);
  const ne: (HTMLElement | null)[] = new Array(maryBoard.length);
  for (let k = 0; k < order.length; k++) {
    nv[k] = maryBoard[order[k]];
    ne[k] = maryEls[order[k]];
  }
  maryBoard = nv;
  maryEls = ne;
}

async function maryResolve(): Promise<void> {
  let chain = 0;
  for (;;) {
    const m = findMatches(maryBoard);
    if (m.size === 0) break;
    chain++;
    maryTierCleared += m.size;
    maryTotalCleared += m.size;
    maryUpdateHud();
    chain >= 3 ? sndCorrect() : sndTick();
    m.forEach((idx) => {
      if (maryEls[idx]) maryEls[idx]!.classList.add('clearing');
    });
    await m3delay(200);
    if (!maryAlive) return;
    m.forEach((idx) => {
      if (maryEls[idx]) maryEls[idx]!.remove();
      maryEls[idx] = null;
      maryBoard[idx] = null;
    });
    maryCollapseDOM();
    maryRenderAll();
    await m3delay(240);
    if (!maryAlive) return;
  }
}

function endMaryTier(): void {
  if (maryTierCleared >= MARY_TIERS[maryTier].goal) maryGoalsMet++;
  if (maryTier < MARY_TIERS.length - 1) {
    maryTier++;
    setTimeout(() => {
      if (maryAlive) beginMaryTier();
    }, 650);
  } else {
    finishMary();
  }
}

function finishMary(): void {
  if (maryDone) return;
  maryDone = true;
  maryAlive = false;
  marySel = -1;
  byId('marySkip').classList.remove('show');
  const { cents, jackpot } = maryReward(maryTotalCleared, maryGoalsMet);
  if (jackpot) ctx.addAchievement('🌹', 'Crown of Roses', 'Completed all three tiers of the Mother Mary Match-3');
  ctx.engine?.awardBonus(cents, maryGoalsMet + ' of ' + MARY_TIERS.length + ' tiers', 'Mary');
  ctx.updateBank();
  ctx.floatDelta(cents);
  if (jackpot) {
    sndJackpot();
    applause();
  } else {
    sndCorrect();
  }
  const msg = jackpot
    ? 'All three tiers — a crown of roses! 🌹'
    : `Cleared ${maryTotalCleared} across ${maryGoalsMet} tier${maryGoalsMet === 1 ? '' : 's'}`;
  byId('maryResult').innerHTML = `<div class="r-amt ${jackpot ? 'fast' : ''}">${jackpot ? 'BINGO! ' + fmtDelta(cents) : fmtDelta(cents)}</div><div class="r-sub">${msg}</div>`;
  const btn = byId<HTMLButtonElement>('maryBtn');
  btn.textContent = 'Continue';
  btn.disabled = false;
  btn.onclick = () => {
    btn.onclick = startMary;
    ctx.nextQuestion();
  };
}

export function skipMary(): void {
  if (!maryAlive || maryDone) return;
  maryDone = true;
  maryAlive = false;
  marySel = -1;
  ctx.engine?.awardBonus(SKIP_AWARD, 'a peaceful pass', 'Skip');
  ctx.updateBank();
  ctx.floatDelta(SKIP_AWARD);
  sndCorrect();
  byId('maryResult').innerHTML = `<div class="r-amt">${fmtDelta(SKIP_AWARD)}</div><div class="r-sub">Skipped. On to the next round.</div>`;
  const btn = byId<HTMLButtonElement>('maryBtn');
  btn.textContent = 'Continue';
  btn.disabled = false;
  btn.onclick = () => {
    btn.onclick = startMary;
    ctx.nextQuestion();
  };
  byId('marySkip').classList.remove('show');
}
